import * as React from 'react';
import moment from 'moment/moment';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import {
	BookingPricingDiagnostic,
	DiagnosticAlteration,
} from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { pricingLineLabel } from './pricingLineLabels';

export interface PricingDiagnosticPanelProps {
	diagnostic: BookingPricingDiagnostic | null;
}

function money(value: number | null): string {
	return value == null ? '—' : formatPriceDisplay(value, false, true, true);
}

/**
 * The price-list snapshot, pretty-printed only once someone opens it.
 *
 * <details> keeps its children in the DOM whether it is open or not, so stringifying the snapshot inline
 * built a full pretty-printed copy of the tenant's price book — every vehicle band, trailer band, freight
 * category, type and handling row — for EVERY alteration before the modal could paint. A ten-alteration
 * booking paid that cost ten times over to show ten collapsed rows. Now the dump is built on first open and
 * kept, so opening it twice is free.
 */
function PriceListSnapshot({ priceList }: { priceList: Record<string, unknown> }) {
	const [opened, setOpened] = React.useState(false);

	return (
		<details
			className="pricing-diagnostic__pricelist"
			onToggle={event => {
				if ((event.currentTarget as HTMLDetailsElement).open) {
					setOpened(true);
				}
			}}
		>
			<summary>Price list snapshot</summary>
			{opened && <pre>{JSON.stringify(priceList, null, 2)}</pre>}
		</details>
	);
}

function AlterationSection({ alteration, index }: { alteration: DiagnosticAlteration; index: number }) {
	/* The trip this alteration was priced against, which is not necessarily the booking's current trip — an
	   edit can move it to another trip, another day and another leg, and that is often the reason the price
	   moved. `routeName` is the alteration's own stops ("Location 1 to Location 2"), which on a route with
	   intermediate stops is the leg the customer actually travelled. A cancellation is blank and carries no
	   trip, so the line is simply absent there. The trip's own name only earns a place when it says something
	   the leg does not. */
	const tripParts = [
		alteration.routeName,
		alteration.tripDeparture ? moment(alteration.tripDeparture).format('ddd DD MMM YYYY, HH:mm') : null,
		alteration.tripName && alteration.tripName !== alteration.routeName ? alteration.tripName : null,
	].filter((part): part is string => !!part);

	return (
		<section className="pricing-diagnostic__alteration">
			<div className="pricing-diagnostic__alteration-header">
				<div className="pricing-diagnostic__alteration-header-row">
					<h4 className="pricing-diagnostic__alteration-title">
						{`Alteration ${index + 1} · ${alteration.isAlteration ? 'edit' : 'creation'} · ${alteration.status}`}
					</h4>
					<span className="pricing-diagnostic__alteration-date">
						{moment(alteration.created).format('DD MMM YYYY, HH:mm')}
					</span>
				</div>
				{tripParts.length > 0 && (
					<span className="pricing-diagnostic__alteration-trip">{tripParts.join(' · ')}</span>
				)}
			</div>

			{alteration.tickets.length > 0 && (
				<>
				<span className="pricing-diagnostic__subhead">Tickets</span>
				<table className="pricing-diagnostic__tickets">
					<thead>
						<tr>
							<th>Type</th>
							<th>Detail</th>
							<th>Count</th>
							<th>Price</th>
							<th>Configuration</th>
						</tr>
					</thead>
					<tbody>
						{alteration.tickets.map((ticket, i) => (
							<tr key={i}>
								<td>{ticket.type}</td>
								<td>{ticket.detail ?? '—'}</td>
								<td>{ticket.amount ?? '—'}</td>
								<td>{money(ticket.price)}</td>
								<td className="pricing-diagnostic__config">{ticket.configuration ?? '—'}</td>
							</tr>
						))}
					</tbody>
				</table>
				</>
			)}

			{(alteration.lineItems.length > 0
				|| alteration.fees.length > 0
				|| alteration.discounts.length > 0
				|| alteration.isCancellation) && (
				<div className="pricing-diagnostic__lines">
					{/* Deliberately shown next to the stored tickets rather than instead of them. These are NOT
					    the same numbers: on an edit these are DELTAS against the previous alteration while the
					    tickets are absolute; the passenger line has the vehicle-included allowance applied
					    while the ticket row shows the raw count and fare; and a line is zeroed when its guard
					    fails (no trip, unloaded navigation) even though the ticket still holds a price. Where
					    the two disagree is the whole diagnostic.

					    A CANCELLATION is the exception: its alteration is blank, so there is nothing to price
					    and these lines are the money it moved instead — the refund and any credit voucher, off
					    the cancellation transaction, with the cancellation fee alongside. */}
					<span className="pricing-diagnostic__subhead">
						{alteration.isCancellation
							? 'What the cancellation moved'
							: alteration.isAlteration
								? 'What pricing charges — change since the previous alteration'
								: 'What pricing charges — this booking'}
					</span>
					{alteration.isCancellation && alteration.lineItems.length === 0 && alteration.fees.length === 0 && (
						<p className="pricing-diagnostic__note">
							No refund, credit voucher or cancellation fee was recorded against this cancellation.
						</p>
					)}
					{alteration.lineItems.map((line, i) => (
						<div className="pricing-diagnostic__line" key={i}>
							<span>{pricingLineLabel(line.key, line.description)}</span>
							<span>{money(line.amount)}</span>
						</div>
					))}
					{/* Fees sit with the products rather than in their own table — they are part of what the
					    booking costs. Only ONE class actually reaches the booking price though (an OTHER fee
					    with a refundable option, which the summary folds in), so the rest are marked and left
					    out of the total instead of being silently added or silently hidden. */}
					{alteration.fees.map((fee, i) => {
						// `inBookingPrice` answers "is this folded into the BOOKING price", which is the right
						// question on a creation or an edit. A cancellation has no booking price left to be
						// part of — its fee is the charge — so on that alteration every fee counts and none is
						// marked as excluded.
						const counted = alteration.isCancellation || fee.inBookingPrice;

						return (
							<div
								className={`pricing-diagnostic__line${counted ? '' : ' pricing-diagnostic__line--excluded'}`}
								key={`fee-${i}`}
							>
								<span>
									{fee.feeOption ?? `${fee.feeType} fee`}
									{!counted && <em> — not in the booking price</em>}
								</span>
								<span>{money(fee.amount)}</span>
							</div>
						);
					})}
					{alteration.discounts.map((discount, i) => (
						<div className="pricing-diagnostic__line" key={`discount-${i}`}>
							<span>
								{discount.label}
								{/* A discount cannot take an alteration below zero, so what came off is not always
								    what was recorded. Show the stored figure when the clamp moved it — otherwise the
								    line looks wrong against the promotion's configured value. */}
								{Math.abs(discount.amount) !== discount.storedAmount && (
									<em> — {money(discount.storedAmount)} recorded, capped at what was left</em>
								)}
							</span>
							<span>{money(discount.amount)}</span>
						</div>
					))}
					<div className="pricing-diagnostic__line pricing-diagnostic__line--total">
						<span>{alteration.isCancellation ? 'Net' : 'Total'}</span>
						<span>
							{money(
								alteration.lineItems.reduce((sum, line) => sum + (line.amount ?? 0), 0)
								+ alteration.discounts.reduce((sum, discount) => sum + discount.amount, 0)
								+ alteration.fees
									.filter(fee => alteration.isCancellation || fee.inBookingPrice)
									.reduce((sum, fee) => sum + (fee.amount ?? 0), 0),
							)}
						</span>
					</div>
				</div>
			)}

			{alteration.priceList && <PriceListSnapshot priceList={alteration.priceList} />}
		</section>
	);
}

/**
 * The raw per-alteration pricing diagnostic. Presentational only — see {@link PriceBreakdownPanel} for why
 * the fetch and the modal chrome live in the parent.
 */
export function PricingDiagnosticPanel({ diagnostic }: PricingDiagnosticPanelProps) {
	const alterations = diagnostic?.alterations ?? [];

	if (alterations.length === 0) {
		return <p className="pricing-diagnostic__empty">No pricing data is available for this booking.</p>;
	}

	return (
		<div className="pricing-diagnostic">
			{alterations.map((alteration, index) => (
				<AlterationSection key={alteration.id} alteration={alteration} index={index} />
			))}
		</div>
	);
}
